/**
 * Comprehensive email validation utility
 * Validates email format, common typos, and provides helpful feedback
 */

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

/**
 * Common email domain typos and their corrections
 */
const COMMON_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaiil.com': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmaiil.com': 'hotmail.com',
  'comapny.com': 'company.com',
  'compnay.com': 'company.com',
  'compan.com': 'company.com',
};

/**
 * Common TLD typos
 */
const COMMON_TLD_TYPOS: Record<string, string> = {
  '.con': '.com',
  '.cmo': '.com',
  '.ocm': '.com',
  '.co': '.com',
  '.cm': '.com',
  '.om': '.com',
};

/**
 * Validates email format using RFC 5322 compliant regex
 */
function isValidEmailFormat(email: string): boolean {
  // More comprehensive email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
}

/**
 * Checks for common typos in email domains
 */
function checkDomainTypo(email: string): { hasTypo: boolean; suggestion?: string } {
  const [localPart, domain] = email.toLowerCase().split('@');
  
  if (!domain) {
    return { hasTypo: false };
  }

  // Check for domain typos
  if (COMMON_DOMAIN_TYPOS[domain]) {
    return {
      hasTypo: true,
      suggestion: `${localPart}@${COMMON_DOMAIN_TYPOS[domain]}`
    };
  }

  // Check for TLD typos
  const domainParts = domain.split('.');
  if (domainParts.length >= 2) {
    const tld = '.' + domainParts[domainParts.length - 1];
    if (COMMON_TLD_TYPOS[tld]) {
      const correctedDomain = domainParts.slice(0, -1).join('.') + COMMON_TLD_TYPOS[tld];
      return {
        hasTypo: true,
        suggestion: `${localPart}@${correctedDomain}`
      };
    }
  }

  return { hasTypo: false };
}

/**
 * Validates email and provides helpful feedback
 */
export function validateEmail(email: string): EmailValidationResult {
  // Trim whitespace
  const trimmedEmail = email.trim();

  // Check if empty
  if (!trimmedEmail) {
    return {
      isValid: false,
      error: 'Email address is required'
    };
  }

  // Check for spaces
  if (trimmedEmail.includes(' ')) {
    return {
      isValid: false,
      error: 'Email address cannot contain spaces'
    };
  }

  // Check for @ symbol
  if (!trimmedEmail.includes('@')) {
    return {
      isValid: false,
      error: 'Email address must contain @ symbol'
    };
  }

  // Check for multiple @ symbols
  const atCount = (trimmedEmail.match(/@/g) || []).length;
  if (atCount > 1) {
    return {
      isValid: false,
      error: 'Email address can only contain one @ symbol'
    };
  }

  // Check format
  if (!isValidEmailFormat(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }

  // Check for common typos
  const typoCheck = checkDomainTypo(trimmedEmail);
  if (typoCheck.hasTypo && typoCheck.suggestion) {
    return {
      isValid: false,
      error: `Did you mean "${typoCheck.suggestion}"?`,
      suggestion: typoCheck.suggestion
    };
  }

  // Additional checks
  const [localPart, domain] = trimmedEmail.split('@');
  
  // Check local part length
  if (localPart.length === 0) {
    return {
      isValid: false,
      error: 'Email address must have a username before @'
    };
  }

  if (localPart.length > 64) {
    return {
      isValid: false,
      error: 'Email username is too long (maximum 64 characters)'
    };
  }

  // Check domain
  if (!domain || domain.length === 0) {
    return {
      isValid: false,
      error: 'Email address must have a domain after @'
    };
  }

  if (domain.length > 255) {
    return {
      isValid: false,
      error: 'Email domain is too long'
    };
  }

  // Check for consecutive dots
  if (trimmedEmail.includes('..')) {
    return {
      isValid: false,
      error: 'Email address cannot contain consecutive dots'
    };
  }

  // Check if domain starts or ends with dot
  if (domain.startsWith('.') || domain.endsWith('.')) {
    return {
      isValid: false,
      error: 'Email domain cannot start or end with a dot'
    };
  }

  // Check if local part starts or ends with dot
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return {
      isValid: false,
      error: 'Email username cannot start or end with a dot'
    };
  }

  // All checks passed
  return {
    isValid: true
  };
}

/**
 * Validates that two emails match (for confirmation fields)
 */
export function validateEmailMatch(email1: string, email2: string): EmailValidationResult {
  const trimmed1 = email1.trim().toLowerCase();
  const trimmed2 = email2.trim().toLowerCase();

  if (!trimmed1 || !trimmed2) {
    return {
      isValid: false,
      error: 'Both email fields are required'
    };
  }

  if (trimmed1 !== trimmed2) {
    return {
      isValid: false,
      error: 'Email addresses do not match. Please check and try again.'
    };
  }

  return {
    isValid: true
  };
}

/**
 * Normalizes email (lowercase, trim)
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

